import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { includes } from 'lodash';
import { Button, ButtonsGroup, extractErrorMessage, Layout, MetricItemAdvance, Text, WidgetDivider } from '@netapp/bxp-design-system-react';
import { notificationSettings as i18nNamespace } from '../../../../i18n/namespace';
import { apiClient } from "utils/clients";
import { ConfigurationBaseCard, ConfigurationBaseFooter } from "modules/console/configurations";
import { isAllowed } from "utils/permissions";
import PermissionComponent from "modules/app/components/permission/PermissionComponent";
import { history } from "store";
import { trackConsoleFullStoryEvent } from "utils/mrTracker";
import { ReactComponent as WebhookIcon } from './../../assets/icon-webhook-2color.svg';
import styles from '../../style.module.scss';

const WebhookConfigurationCard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [webhookCount, setWebhookCount] = useState(0);
    const { selectedOrg: { rules } = {} } = useSelector(state => state.tenancy);
    const [error, setError] = useState(null);
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const canViewCard = isAllowed(rules, 'notifications', 'access');
    const { selectedOrgId } = useSelector(state => state.tenancy);

    useEffect(() => {
        const fetchWebhooks = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/webhooks`);
                if (response) {
                    setWebhookCount(response.data?.items?.length || 0);
                }
            } catch (error) {
                setWebhookCount(0);
                setError(extractErrorMessage(error, 'description'));
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: t('failed_load_webhooks', i18nNamespace),
                        exception: error,
                        moreInfo: extractErrorMessage(error, 'description'),
                        key: 'FETCH-WEBHOOKS',
                        showGlobal: true
                    }
                });
            } finally {
                setIsLoading(false);
            }
        };
        if (canViewCard) {
            fetchWebhooks();
        }
    }, [canViewCard, selectedOrgId]);

    return (
        <ConfigurationBaseCard title={t('webhook_integration')}
            iconRenderer={() => <WebhookIcon size='24px' />}
            isLoading={isLoading}
            hasNoPermission={!canViewCard}
            hasError={!!error} >
            <Layout.Grid rowGap='lg'>
                {(webhookCount === 0) && <Layout.GridItem lg={12}>
                    <Text>{t('webhook_integration_desc')}</Text>
                </Layout.GridItem>}
                {(webhookCount > 0) && (
                    <Layout.GridItem lg={12} className={styles.flexRowAlignEnd}>
                        <MetricItemAdvance
                            className={styles.metricPaddingRight5}
                            isLoading={isLoading}
                            label={webhookCount > 1 ? t('webhooks_configured_plural', { count: webhookCount }) : t('webhooks_configured', { count: webhookCount })}
                            labelTooltip={t('webhooks_configured')}
                            valueSize="small"
                            value={webhookCount}
                            valueTooltip={webhookCount}
                            valueClassName={styles.valueLineHeight24}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                )}
            </Layout.Grid>
            {(
                <ConfigurationBaseFooter style={{ justifyContent: 'flex-start', borderTop: '0px', alignItems: 'flex-end', marginTop: 'auto' }}>
                    <ButtonsGroup>
                        <PermissionComponent hasPermssion={(webhookCount === 0) ? includes(rules, 'bxp:notifications-create') : true}>
                             <Button color="secondary" className={styles.width152} onClick={() => {
                                if (webhookCount === 0) {
                                    trackConsoleFullStoryEvent({
                                        name: 'Webhook-Add-Started',
                                        payload: {}
                                    });
                                }
                                history.push((webhookCount > 0) ? `/console-configurations/webhooks` : `/console-configurations/webhooks/add`);
                             }} >
                                {webhookCount > 0 ? t('view') : t('configure')}
                            </Button>
                        </PermissionComponent>
                    </ButtonsGroup>
                </ConfigurationBaseFooter>
            )}
        </ConfigurationBaseCard>
    )
}

export default React.memo(WebhookConfigurationCard);